/**
 * API Service - 统一 API 调用
 */

import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 添加 Token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token 过期，移除并跳转登录
      localStorage.removeItem('token');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// ===== 活动 API =====

export const eventApi = {
  // 获取活动列表
  list: (params?: any) => api.get('/public/events', { params }),
  
  // 获取即将举行的活动
  upcoming: () => api.get('/public/events/upcoming'),
  
  // 获取活动详情
  get: (id: number) => api.get(`/public/events/${id}`),
  
  // 获取活动定价
  getPricing: (id: number) => api.get(`/public/events/${id}/pricing`),
};

// ===== 管理员活动 API =====

export const adminEventApi = {
  list: (params?: any) => api.get('/admin/events', { params }),
  get: (id: number) => api.get(`/admin/events/${id}`),
  create: (data: any) => api.post('/admin/events', data),
  update: (id: number, data: any) => api.put(`/admin/events/${id}`, data),
  delete: (id: number) => api.delete(`/admin/events/${id}`),
  stats: () => api.get('/admin/events/stats'),
};

// ===== 报名 API =====

export const registrationApi = {
  // 公开报名
  publicCreate: (data: any) => api.post('/public/registrations', data),
  
  // 通过邮箱和活动ID获取报名记录
  getByEmailAndEvent: (email: string, eventId: number) => 
    api.get('/public/registrations', { params: { email, event_id: eventId } }),
  
  // 管理员
  list: (params?: any) => api.get('/admin/registrations', { params }),
  get: (id: number) => api.get(`/admin/registrations/${id}`),
  update: (id: number, data: any) => api.patch(`/admin/registrations/${id}`, data),
  delete: (id: number) => api.delete(`/admin/registrations/${id}`),
  export: (params?: any) => api.get('/admin/registrations/export', { params, responseType: 'blob' }),
  batchApprove: (ids: number[]) => api.patch('/admin/registrations/batch-approve', { ids }),
  batchReject: (ids: number[]) => api.patch('/admin/registrations/batch-reject', { ids }),
};

// ===== 支付 API =====

export const paymentApi = {
  // 获取 Stripe 公钥
  getPublicKey: () => api.get('/public/stripe/public-key'),
  
  // 计算价格预览（后端校验）
  calculatePrice: (params: {
    event_id: number;
    email?: string;
    code?: string;
    wallet_address?: string;
  }) => api.get('/public/payments/calculate-price', { params }),
  
  // 创建支付会话
  createSession: (data: {
    event_id: number;
    user_email: string;
    user_name?: string;
    user_id?: string;
    registration_id?: number;
    wallet_address?: string;
    code?: string;
    test_mode?: boolean;
  }) => api.post('/public/payments/create-session', data),
  
  // 获取支付状态
  getStatus: (paymentId: number) => api.get(`/public/payments/status/${paymentId}`),
  
  // 通过 Session 获取支付
  getBySession: (sessionId: string) => api.get(`/public/payments/session/${sessionId}`),
  
  // Token 访问请求
  requestTokenAccess: (data: {
    event_id: number;
    wallet_address: string;
    email: string;
  }) => api.post('/public/token-access/request', data),
  
  // 验证 Token 持有者
  verifyTokenHolder: (data: {
    event_id: number;
    wallet_address: string;
    code: string;
  }) => api.post('/public/token-access/verify', data),
  
  // 管理员：获取 Token 持有者
  getTokenHolders: (eventId: number) => api.get(`/admin/token-holders/event/${eventId}`),
  
  // 管理员：添加 Token 持有者
  addTokenHolder: (data: {
    event_id: number;
    wallet_address: string;
    email: string;
    token_symbol: string;
    token_amount: number;
    notes?: string;
  }) => api.post('/admin/token-holders', data),
  
  // 管理员：创建退款
  createRefund: (data: {
    payment_id: number;
    amount: number;
    reason: string;
  }) => api.post(`/admin/payments/${data.payment_id}/refund`, { amount: data.amount, reason: data.reason }),
};

// ===== 其他 API =====

export const guestApi = {
  list: (params?: any) => api.get('/public/guests', { params }),
  fields: () => api.get('/public/guests/fields'),
  get: (id: number) => api.get(`/public/guests/${id}`),
};

export const donorApi = {
  list: (params?: any) => api.get('/public/donors', { params }),
  get: (id: number) => api.get(`/public/donors/${id}`),
};

export const mediaApi = {
  list: (params?: any) => api.get('/public/media', { params }),
  get: (id: number) => api.get(`/public/media/${id}`),
};

export const teamApi = {
  list: (params?: any) => api.get('/public/teams', { params }),
  get: (id: number) => api.get(`/public/teams/${id}`),
};

export const reportApi = {
  list: (params?: any) => api.get('/public/reports', { params }),
  get: (id: number) => api.get(`/public/reports/${id}`),
};

export const planApi = {
  list: (params?: any) => api.get('/public/plans', { params }),
  get: (id: number) => api.get(`/public/plans/${id}`),
};

export const contactApi = {
  submit: (data: {
    name: string;
    email: string;
    subject: string;
    message: string;
  }) => api.post('/public/contact', data),
};

export const subscriptionApi = {
  subscribe: (email: string) => api.post('/public/subscriptions', { email }),
  unsubscribe: (token: string) => api.get('/public/subscriptions/unsubscribe', { params: { token } }),
};

// ===== 管理员 API =====

export const adminStaffApi = {
  list: (params?: any) => api.get('/admin/staff', { params }),
  get: (id: number) => api.get(`/admin/staff/${id}`),
  create: (data: any) => api.post('/admin/staff', data),
  update: (id: number, data: any) => api.put(`/admin/staff/${id}`, data),
  delete: (id: number) => api.delete(`/admin/staff/${id}`),
  resetPassword: (id: number) => api.post(`/admin/staff/${id}/reset-password`),
};

export const adminRoleApi = {
  list: () => api.get('/admin/roles'),
  get: (id: number) => api.get(`/admin/roles/${id}`),
};

export const adminGuestApi = {
  list: (params?: any) => api.get('/admin/guests', { params }),
  get: (id: number) => api.get(`/admin/guests/${id}`),
  create: (data: any) => api.post('/admin/guests', data),
  update: (id: number, data: any) => api.put(`/admin/guests/${id}`, data),
  delete: (id: number) => api.delete(`/admin/guests/${id}`),
};

export const adminDonorApi = {
  list: (params?: any) => api.get('/admin/donors', { params }),
  get: (id: number) => api.get(`/admin/donors/${id}`),
  create: (data: any) => api.post('/admin/donors', data),
  update: (id: number, data: any) => api.put(`/admin/donors/${id}`, data),
  delete: (id: number) => api.delete(`/admin/donors/${id}`),
};

export const adminMediaApi = {
  list: (params?: any) => api.get('/admin/media', { params }),
  get: (id: number) => api.get(`/admin/media/${id}`),
  create: (data: any) => api.post('/admin/media', data),
  update: (id: number, data: any) => api.put(`/admin/media/${id}`, data),
  delete: (id: number) => api.delete(`/admin/media/${id}`),
  toggleFeatured: (id: number) => api.patch(`/admin/media/${id}/toggle-featured`),
};

export const adminAuditApi = {
  list: (params?: any) => api.get('/admin/audit', { params }),
  export: (params?: any) => api.get('/admin/audit/export', { params, responseType: 'blob' }),
};
